import { Address, Asset } from 'nftx.js';
import { useTranslations } from 'use-intl';
import AssetImage from '@/components/elements/AssetImage';
import Collapsible from '@/components/modules/Collapsible/Collapsible';
import ActionButton from '@/components/elements/ActionButton/ActionButton';
import FormattedEther from '../../elements/FormattedEther';
import { Dispatch, ReactNode, useMemo } from 'react';
import { CreateInventoryPositionDetails, Step } from './types';
import Button from '../../elements/Button';
import Icon, { Icons } from '../../elements/Icon';

export interface SidebarProps {
  assetName: string;
  assetAddress: Address;
  assets: Asset[];
  selectedItems: CreateInventoryPositionDetails['items'];
  hasExistingPosition: boolean;
  withinModal?: boolean;
  currentApr: bigint;
  currentShare: bigint;
  newApr: bigint;
  newShare: bigint;
  dispatch: Dispatch<Partial<CreateInventoryPositionDetails>>;
  relativeLockTime: string;
  using: 'nft' | 'vtoken';
  vTokenAmount: string;
  step: Step;
  onPrevious: () => void;
  Processing: ReactNode;
  Success: ReactNode;
}

export const Sidebar = ({
  assetName,
  assetAddress,
  assets,
  selectedItems,
  vTokenAmount,
  hasExistingPosition,
  withinModal = false,
  dispatch,
  currentApr,
  currentShare,
  newApr,
  newShare,
  relativeLockTime,
  using,
  step,
  Processing,
  Success,
  onPrevious,
}: SidebarProps) => {
  const t = useTranslations('CreateInventoryPosition');
  const isIdle = step !== 'processing' && step !== 'success';
  const canGoBack = isIdle && step !== 'using';
  const isDisabled = useMemo(() => {
    if (using === 'nft') {
      return selectedItems.length === 0;
    }
    return !vTokenAmount || !Number(vTokenAmount);
  }, [selectedItems.length, using, vTokenAmount]);

  return (
    <aside className="col-span-1">
      <div className={`sticky ${withinModal ? 'top-12' : 'top-24'} space-y-4`}>
        {isIdle && (
          <>
            <Collapsible
              open={true}
              header={
                <span className="flex w-full justify-between font-bold items-center">
                  <span className="uppercase mr-4">{t('basket.staking')}</span>
                  <span className="bg-gradient-pink-orange bg-clip-text text-transparent truncate">
                    {t('basket.count', {
                      count:
                        (using === 'nft'
                          ? selectedItems.length
                          : vTokenAmount) || '--',
                      asset: assetName,
                    })}
                  </span>
                </span>
              }
            >
              <div className="p-4">
                {!!(selectedItems.length && using === 'nft') && (
                  <section className="grid grid-cols-8 gap-1 mb-4 max-h-28 hide-scroll overflow-y-scroll border-b border-gray-300 pb-4">
                    {selectedItems.map((item, i) => {
                      const asset = assets.find(
                        (asset) => asset.tokenId === item.tokenId
                      );

                      return (
                        <button
                          type="button"
                          key={`${item.tokenId}${i}`}
                          className="rounded-sm overflow-hidden hover:opacity-75 relative group"
                          onClick={() =>
                            dispatch({
                              items: selectedItems.filter(
                                (x) => x.tokenId !== item.tokenId
                              ),
                            })
                          }
                        >
                          <AssetImage
                            assetAddress={assetAddress}
                            {...item}
                            {...asset}
                          />
                          <span className="opacity-0 group-hover:opacity-100 absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-70 text-gray-50 text-xs font-bold">
                            <Icon name={Icons.X_MARK} size={20} />
                          </span>
                        </button>
                      );
                    })}
                  </section>
                )}
                <dl className="space-y-4">
                  <div className="flex items-baseline space-x-3">
                    <dt>{t(`basket.apr` as any)}</dt>
                    <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
                    <dd className="text-right text-gray-800 whitespace-nowrap truncate">
                      <FormattedEther
                        value={currentApr}
                        style="percent"
                        minimumFractionDigits={2}
                      />
                      {currentApr !== newApr && (
                        <>
                          {' → '}
                          <FormattedEther
                            value={newApr}
                            style="percent"
                            minimumFractionDigits={2}
                          />
                        </>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-baseline space-x-3">
                    <dt>{t(`basket.share` as any)}</dt>
                    <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
                    <dd className="text-right text-gray-800 whitespace-nowrap truncate">
                      <FormattedEther
                        value={currentShare}
                        style="percent"
                        minimumFractionDigits={2}
                      />
                      {currentShare !== newShare && (
                        <>
                          {' → '}
                          <FormattedEther
                            value={newShare}
                            style="percent"
                            minimumFractionDigits={2}
                          />
                        </>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-baseline space-x-3">
                    <dt>{t(`basket.lock` as any)}</dt>
                    <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
                    <dd className="text-right text-pink-600 font-bold whitespace-nowrap truncate">
                      {relativeLockTime}
                    </dd>
                  </div>
                </dl>
              </div>
            </Collapsible>
            {step === 'using' ? (
              <Button type="submit" className="w-full">
                {t('continue')}
              </Button>
            ) : (
              <ActionButton
                type="submit"
                blendColor="after:bg-gray-100"
                className="w-full"
                disabled={isDisabled}
              >
                {t('create')}
              </ActionButton>
            )}
            {canGoBack && (
              <div className="text-center">
                <button
                  type="button"
                  className="text-gray-500 underline hover:no-underline inline-block text-sm"
                  onClick={onPrevious}
                >
                  {t('back')}
                </button>
              </div>
            )}
            {hasExistingPosition && (
              <p className="font-medium text-xs text-center text-gray-400 max-w-prose px-4">
                {t('mergeInfo')}
              </p>
            )}
          </>
        )}
        {Processing}
        {Success}
      </div>
    </aside>
  );
};

export default Sidebar;
